/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { Box, Typography, Avatar, AvatarGroup } from '@quillbot/design-system';
import { QuillTabs, QuillTab } from '@Common/quillElements/QuillTabs';
import Link from 'next/link';
import BoltIcon from '@mui/icons-material/Bolt';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';

import { tabContent, benefitsData } from './data';
import {
  StyledButton,
  StyledParaTypo,
  StyledContainer,
  StyledTitleTypo,
  StyledCheckBox,
  StyledTabPanel,
  StyledQuotesTypo,
} from './StyledComponents';
import Highlight from '../Highlight';
import Benefits from '../Benefits';
import StepsBlock from './components/StepsBlock';

function AIPillar() {
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const isTablet = useSelector((state: RootState) => state.common.platform.isTablet);

  const TAB_MAP_ID = {
    TAB_1: 'TAB_1',
    TAB_2: 'TAB_2',
    TAB_3: 'TAB_3',
    TAB_4: 'TAB_4',
    TAB_5: 'TAB_5',
  };
  const [tab, setTab] = useState(TAB_MAP_ID.TAB_1);

  function getTabContent() {
    switch (tab) {
      case TAB_MAP_ID.TAB_1:
        return tabContent.TAB_1;
      case TAB_MAP_ID.TAB_2:
        return tabContent.TAB_2;
      case TAB_MAP_ID.TAB_3:
        return tabContent.TAB_3;
      case TAB_MAP_ID.TAB_4:
        return tabContent.TAB_4;
      case TAB_MAP_ID.TAB_5:
        return tabContent.TAB_5;
      default:
        return tabContent.TAB_1;
    }
  }
  return (
    <>
      <StyledContainer px={{ xs: '42px', md: 0 }}>
        <Box pt={{ md: '68px', xs: '36px' }} />
        <Typography
          textAlign="center"
          component="h1"
          color="text.primary"
          fontSize={{ md: '30px', xs: '24px' }}
          fontWeight={400}
        >
          QuillBot AI - Best AI Writing Assistant for 2022
        </Typography>
        <Box pt="28px" />
        <Box display="flex" justifyContent="center" alignItems="center">
          <AvatarGroup max={3}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                border: '1px solid !important',
                borderColor: 'text.primary !important',
              }}
              alt="Paige Pfeifer"
              src="https://quillbot.com/blog/content/images/2021/12/Paige-Pfeifer---Author-at-QuillBot-1.jpeg"
            />
            <Avatar
              sx={{
                width: 32,
                height: 32,
                border: '1px solid !important',
                borderColor: 'text.primary !important',
              }}
              alt="Sanhita Agarwal"
              src="https://quillbot.com/blog/content/images/2021/12/Sanhita-Agarwal---Head-of-SEO--QuillBot-.png"
            />
          </AvatarGroup>
          <Box fontStyle="italic">
            &nbsp;by&nbsp;
            <a
              href="https://quillbot.com/blog/author/paige/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Paige Pfeifer
            </a>
            ,&nbsp;
            <a
              href="https://quillbot.com/blog/author/sanhita/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sanhita Agarwal
            </a>
          </Box>
        </Box>
        <Box pt="28px" />
        <Box textAlign="center">
          <a target="_blank" rel="noreferrer" href="/">
            <StyledButton
              sx={{ borderRadius: '20px', fontSize: '17px', fontWeight: 600, px: '24px' }}
              color="primary"
              variant="contained"
            >
              Try Paraphrasing
            </StyledButton>
          </a>
        </Box>
        <Box pt={{ md: '68px', xs: '32px' }} />
        <StyledParaTypo>
          If the phrase “artificial intelligence” has you conjuring up images of Hollywood robots
          and lab rats, you’re not alone (and you have a stellar imagination and are probably super
          cool).
          <br />
          <br />
          The concept sounds futuristic and foreign to a lot of people, but the truth is, if you’ve
          used QuillBot, you’ve used an AI content generator. Not so scary, right?
        </StyledParaTypo>
        <Box pt={{ xs: '32px', sm: '32px', md: '44px' }} />
        <StyledQuotesTypo>
          The AI writer isn&apos;t sinister, and it isn’t out to replace human thinking.{' '}
        </StyledQuotesTypo>
        <Box pt={{ xs: '32px', sm: '32px', md: '44px' }} />
        <StyledParaTypo>
          You might be skeptical, and that’s alright. Let’s dive into the facts about the AI writer
          and see if we can gain a new perspective on this awesome tool.{' '}
        </StyledParaTypo>
        <Box pt={{ xs: '32px', sm: '32px', md: '68px' }} />
        <StyledTitleTypo variant="h2">What is an AI writing assistant?</StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          Let’s make this easy and start with what an AI writing assistant is not, because there are
          a lot of misconceptions out there, and we’d like to start off on the right (artificial)
          foot.
          <br />
          <br />
          An AI writing assistant is not a miracle worker, an evil robot hellbent on taking over the
          world, or a perfect piece of technology. It is not a replacement for putting in work, nor
          an excuse to use when writing doesn’t come out perfectly.
          <br />
          <br />
          <Highlight>
            An AI writing assistant is a tool that you can use to help with and enhance their own
            writing.
          </Highlight>
          &nbsp;Like any tool, its main purpose is{' '}
          <Highlight>to aid in achieving a goal,</Highlight> not to{' '}
          <Highlight>complete the task itself.</Highlight>
        </StyledParaTypo>
        <Box pt={{ xs: '24px', sm: '24px', md: '44px' }} />
        <Box
          height={{ md: 176, xs: 100 }}
          position="relative"
          display="flex"
          px={{ md: '36px', xs: 0 }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
            pr={{ md: '73px', xs: '36px' }}
            borderRadius={{ md: '53px 0  0 53px', xs: '27px 0  0 27px' }}
            bgcolor="rgba(76, 148, 89, 1)"
          >
            <Box textAlign="center" fontSize={0}>
              <BoltIcon sx={{ color: 'grey.50', fontSize: { md: '48px', xs: '32px' } }} />
              <Box pt="8px" />
              <Typography
                fontWeight={600}
                fontSize={{ md: '20px', xs: '12px' }}
                lineHeight={{ md: '27px', xs: '16px' }}
                color="grey.50"
              >
                Write
                <br />
                Faster
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
            pl={{ md: '73px', xs: '36px' }}
            borderRadius={{ md: '0 53px 53px 0', xs: '0 27px 27px 0' }}
            bgcolor="rgba(112, 23, 255, 1)"
          >
            <Box textAlign="center" fontSize={0}>
              <FavoriteBorderIcon sx={{ color: 'grey.50', fontSize: { md: '48px', xs: '32px' } }} />
              <Box pt="8px" />
              <Typography
                fontWeight={600}
                fontSize={{ md: '20px', xs: '12px' }}
                lineHeight={{ md: '27px', xs: '16px' }}
                color="grey.50"
              >
                Writer <br />
                Better
              </Typography>
            </Box>
          </Box>
          <Box
            borderRadius={{ md: '52px', xs: '27px' }}
            bgcolor="rgba(48, 0, 112, 1)"
            position="absolute"
            height="100%"
            width={{ md: '146px', xs: '80px' }}
            display="flex"
            alignItems="center"
            left="50%"
            sx={{
              transform: 'translateX(-50%)',
            }}
          >
            <Box textAlign="center">
              <Box height={{ md: 47, xs: 32 }} width={{ md: 47, xs: 32 }} margin="0 auto">
                <img height="100%" width="100%" src="/images/quill-icon.svg" alt="quill icon" />
              </Box>

              <Box pt="12px" />
              <Typography
                fontWeight={700}
                fontSize={{ md: '20px', xs: '12px' }}
                lineHeight={{ md: '27px', xs: '16px' }}
                color="grey.50"
              >
                Writing Assistant
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box pt={{ xs: '52px', sm: '52px', md: '76px' }} />
        <StyledTitleTypo variant="h2">How does an AI writer work?</StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          Artificial Intelligence, or AI, is the ability for machines to complete repetitive tasks
          that are typically and historically done by human beings. In fact, the automation aspect
          was one of the driving forces for the creation of AI.&nbsp;
          <Highlight>
            In terms of an AI content generator, this means the machine is able to form cohesive
            thoughts and well-structured sentences
          </Highlight>{' '}
          that contain{' '}
          <a target="_blank" rel="noreferrer" href="/grammar-check">
            {' '}
            correct grammar and punctuation.
          </a>
        </StyledParaTypo>
        <br />
        <br />
        <StyledParaTypo>
          <Highlight>AI programs learn through algorithms and large sets of data.</Highlight> AI
          writers look at patterns of speech and sentence structure, which allows them to learn the
          rules of the English language (or whatever language they are writing in).
          <br />
          <br />
          You thought your life revolves around writing? An AI writer literally exists to write.
          Eat, sleep, breathe? More like research, learn, write. Or write, write, write. Right?
        </StyledParaTypo>
        <Box pt={{ xs: '28px', sm: '28px', md: '40px' }} />
        <Box borderRadius="8px" px="32px" py="28px" bgcolor="rgba(247, 249, 251, 1)">
          <StyledTitleTypo variant="h2">How an AI learns</StyledTitleTypo>
          <Box pt="32px" />
          <Box textAlign="center">
            <img width="100%" src="/images/aiPillarPage/ai-diagram.svg" alt="ai diagram" />
          </Box>
        </Box>
        <Box pt={{ xs: '52px', sm: '52px', md: '76px' }} />
        <StyledTitleTypo variant="h2">How to Write Using Artificial Intelligence</StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          Using AI writing tools is really easy. Like, really easy. To show you just how simple this
          process will be for you, we're going to break it down into small steps.
        </StyledParaTypo>
        <Box pt={{ xs: '28px', sm: '28px', md: '40px' }} />
        <StepsBlock />
        <Box pt={{ xs: '28px', sm: '28px', md: '40px' }} />
        <StyledParaTypo>
          Besides generating written content, our Co-Writer will fix your grammar,{' '}
          <a href="/word-counter" rel="noreferrer" style={{ whiteSpace: 'nowrap' }} target="_blank">
            count your words
          </a>
          ,&nbsp;
          <a href="/citation-generator" rel="noreferrer" target="_blank">
            create citations
          </a>
          , and give paraphrase options and a space to keep notes. There is nothing else you'll need
          in order to create quality content.
        </StyledParaTypo>
        <Box pt={{ xs: '52px', sm: '52px', md: '76px' }} />
        <StyledTitleTypo variant="h2">How to Write Using Artificial Intelligence</StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          Okay, so the AI writer seems like the gift that keeps on giving, right? Almost like it's
          too good to be true? Wrong.
          <br />
          <br />
          While there are AI writing tools out there that you have to pay for, there are plenty of
          options that are offered via a free plan (including, yes, QuillBot's Co-Writer!).
          <br />
          <br />
          In the case of our Co-Writer, the quality is there, with no credit card required. Just
          because a tool is free, doesn't mean it's not as good as the paid tools. Because QuillBot
          offers Premium plans, we're able to offer certain tools completely free.
          <br />
          <br />
          Good, efficient writing shouldn't be accessible only to those with money. Anyone can be a
          good writer, so everyone should have access to the tools that will help them improve their
          writing skills.
        </StyledParaTypo>
        <Box pt={{ xs: '28px', sm: '28px', md: '44px' }} />
        <StyledQuotesTypo>
          "Make writing painless" isn't just a catchy slogan━it's what we believe, and what we're
          set out to do.
        </StyledQuotesTypo>
        <Box pt={{ xs: '52px', sm: '52px', md: '76px' }} />
        <StyledTitleTypo variant="h2">Benefits of Using AI Writing Tools</StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          There are so many benefits to using an AI writer, it's hard to even know where to start!
          Perhaps making a list is the best way to go here. Lay it all out, nice and clear.
          <br />
          <br />
          Here are the benefits of using an AI writer:
        </StyledParaTypo>
        <Box pt={{ xs: '28px', sm: '28px', md: '32px' }} />
      </StyledContainer>

      <Box>
        <Benefits data={benefitsData} />
      </Box>
      <Box bgcolor="#F7F9FB">
        <StyledContainer px={{ xs: '42px', md: 0 }}>
          <Box pt={{ xs: '32px', sm: '32px', md: '60px' }} />
          <StyledTitleTypo variant="h2">Who should I use an AI writer?</StyledTitleTypo>
          <Box pt="14px" />
          <StyledParaTypo>
            The short answer: anyone who wants to write quality content.
            <br />
            <br />
            The long(er), more specific answer:{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://quillbot.com/blog/how-to-become-a-copywriter/"
            >
              copywriters
            </a>
            , students, bloggers, professionals, and everyone in between. Don't believe us? Let's
            take a look at how each of these people will benefit from using an AI writer.
          </StyledParaTypo>
          <Box pt="32px" />
          <Box textAlign="center" mx={{ xs: '-30px', md: 0 }}>
            <QuillTabs>
              <QuillTab onClick={() => setTab(TAB_MAP_ID.TAB_1)} active={tab === TAB_MAP_ID.TAB_1}>
                Professionals
              </QuillTab>
              <QuillTab onClick={() => setTab(TAB_MAP_ID.TAB_2)} active={tab === TAB_MAP_ID.TAB_2}>
                Students
              </QuillTab>
              <QuillTab onClick={() => setTab(TAB_MAP_ID.TAB_3)} active={tab === TAB_MAP_ID.TAB_3}>
                Bloggers
              </QuillTab>
              <QuillTab onClick={() => setTab(TAB_MAP_ID.TAB_4)} active={tab === TAB_MAP_ID.TAB_4}>
                Copywriters
              </QuillTab>
              <QuillTab onClick={() => setTab(TAB_MAP_ID.TAB_5)} active={tab === TAB_MAP_ID.TAB_5}>
                Everyone Else
              </QuillTab>
            </QuillTabs>
          </Box>
        </StyledContainer>

        <Box pt={{ xs: '28px', md: '36px' }} />
        <StyledTabPanel key={tab} data={getTabContent()} />
        <Box pt={{ xs: '36px', sm: '36px', md: '64px' }} />
      </Box>
      <StyledContainer px={{ xs: '42px', md: 0 }}>
        <Box pt={{ xs: '52px', sm: '52px', md: '60px' }} />
        <StyledTitleTypo variant="h2">
          A New AI Article Writer: QuillBot's Co-Writer
        </StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          Decisions, decisions.
          <br />
          <br />
          Yeah, having options can be harder than not having options, sometimes. Which is why we're
          just going to talk about the single best AI writer on the market.
          <br />
          <br />
          Whether you need to write for school, for work, for pleasure, or otherwise,
          <a target="_blank" style={{ whiteSpace: 'nowrap' }} rel="noreferrer" href="/co-writer">
            {' '}
            QuillBot's Co-Writer
          </a>{' '}
          has got you covered (and the best part is, it's free!).
          <br />
          <br />
          The Co-Writer has all of the capabilities of other AI writers out there, plus things that
          make it uniquely a QuillBot tool.
          <br />
          <br />
          <Highlight>
            The Co-Writer can detect tone and predict what should come next in a sentence,{' '}
            <a target="_blank" rel="noreferrer" href="https://quillbot.com/blog/paragraphing/">
              paragraph
            </a>
            , and structure.
          </Highlight>
          <br />
          <br />
          It's perfect if you need to generate articles, social media posts,{' '}
          <a
            rel="noreferrer"
            href="https://quillbot.com/blog/quillbots-guide-to-essay-writing/"
            target="_blank"
          >
            essays
          </a>
          , long form content, product descriptions, emails, or any other type of original content.
        </StyledParaTypo>
        <Box pt={{ xs: '24px', sm: '24px', md: '28px' }} />
        <Box display="flex" flexWrap="wrap" mx="-6px">
          <StyledCheckBox text="Articles" />
          <StyledCheckBox text="Essays" />
          <StyledCheckBox text="Long form content" />
          <StyledCheckBox text="Social Media Posts" />
          <StyledCheckBox text="Emails" />
          <StyledCheckBox text="Product Descriptions" />
        </Box>
        {/* <Box pt="28px" /> */}
        {/* <StyledParaTypo>
          It even has our{' '}
          <a rel="noreferrer" target="_blank" href="/">
            Paraphraser
          </a>
          ,{' '}
          <a rel="noreferrer" target="_blank" href="/summarize">
            Summarizer
          </a>
          , Grammar Checker, and Citation Generator built in, so you never have to click out of the
          Co-Writer itself.
        </StyledParaTypo> */}
        <Box pt={{ xs: '28px', sm: '28px', md: '32px' }} />
        <StyledQuotesTypo>
          It even has our Paraphraser, Summarizer, Grammar Checker, and Citation Generator built in,
          so you never have to click out of the{' '}
          <span style={{ whiteSpace: 'nowrap' }}>Co-Writer</span> itself.
        </StyledQuotesTypo>
        <Box pt="44px" />
        <StyledParaTypo>
          <Highlight>
            Other apps can get the job done, but QuillBot's Co-Writer will do it perfectly.
          </Highlight>{' '}
          The search is over before it even began: go to{' '}
          <a rel="noreferrer" href="https://quillbot.com/co-writer" target="_blank">
            https://quillbot.com/co-writer
          </a>
          &nbsp;to enhance your life with quality writing.
        </StyledParaTypo>
        <Box pt={{ xs: '52px', sm: '52px', md: '60px' }} />
      </StyledContainer>
      <Box py="28px" px={{ md: '28px', xs: 0 }} bgcolor="rgba(48, 0, 112, 1)">
        <StyledContainer px={{ xs: '42px', md: 0 }}>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }}>
            <Typography
              fontSize="17px"
              fontStyle="italic"
              fontWeight={600}
              lineHeight="23px"
              flex={1}
              color="grey.50"
              textAlign={{ xs: 'center', md: 'left' }}
            >
              Check out our quick-start guide for using{' '}
              <span style={{ whiteSpace: 'nowrap' }}>QuillBot's Co-Writer</span> to start writing
              like a pro today.
            </Typography>
            <Box pr={{ md: '12px', xs: 'none' }} pt={{ xs: '28px', md: 'none' }} />
            <Box width="182px" margin={{ xs: '0 auto', md: 'none' }}>
              <a href="/documents/AI Writing_C0-writer.pdf" target="_blank">
                <StyledButton fullWidth variant="contained" color="primary">
                  Download Now
                </StyledButton>
              </a>
            </Box>
          </Box>
        </StyledContainer>
      </Box>
      <Box pt={{ xs: '52px', sm: '52px', md: '74px' }} />
      <StyledContainer px={{ xs: '42px', md: 0 }}>
        <StyledTitleTypo variant="h2">Can the AI writer replace content writers?</StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          Nope.
          <br />
          <br />
          Okay, we can't predict the future. And it's not as simple as a one-word answer. But if
          you're worried about robots taking over your writing-related tasks/job, relax. It's not
          going to happen.
          <br />
          <br />
          <Highlight>AI writers are tools that help us write quality content.</Highlight> However,
          without human beings to program and train them, they cannot accomplish this task. These
          tools mimic the way we write; in order for them to do their jobs, we must do ours.
          Although AI is seen as the way of the future by many, there are still things it cannot
          replicate.
        </StyledParaTypo>
        <Box pt={{ xs: '28px', sm: '28px', md: '44px' }} />
        <StyledQuotesTypo>
          AI can’t replicate human emotion, creativity, and variance. It cannot think like a human,
          only reproduce the patterns it observes.
        </StyledQuotesTypo>
        <Box pt={{ xs: '28px', sm: '28px', md: '44px' }} />
        <StyledParaTypo>
          While there is the possibility that one day, in the far future, AI could replace content
          writers, it's not something that you have to worry about now. If that is something that
          happens, it'll be a while away.
        </StyledParaTypo>
        <Box pt={{ xs: '52px', sm: '52px', md: '76px' }} />
        <StyledTitleTypo variant="h2">The Future of AI-Based Writing</StyledTitleTypo>
        <Box pt="14px" />
        <StyledParaTypo>
          With technology evolving rapidly every day, it's hard to say what the future holds. But
          one thing is for certain: we keep pushing forward and trying new things.
          <br />
          <br />
          <Highlight>
            Things can only get easier from here on out. Content quality can only improve.
            Algorithms can only advance. Your writing process can only streamline further.
          </Highlight>
          <br />
          <br />
          AI is here to stay, and it's time to get with the program.
        </StyledParaTypo>
      </StyledContainer>
      <Box pt={{ xs: '52px', sm: '52px', md: '60px' }} />
    </>
  );
}

export default AIPillar;
